import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/lib/i18n";
import { ArrowLeft, BookmarkPlus, BookmarkCheck, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function ArticleDetail() {
  const { article_id } = useParams();
  const { lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [bookmarked, setBookmarked] = useState(false);

  useEffect(() => {
    api.get(`/articles/${article_id}`).then(({ data }) => setArticle(data));
    if (user) {
      api.get("/bookmarks").then(({ data }) => {
        setBookmarked(data.some((a) => a.article_id === article_id));
      });
    }
  }, [article_id, user]);

  const toggle = async () => {
    if (!user) { navigate("/login"); return; }
    if (bookmarked) {
      await api.delete(`/bookmarks/${article_id}`); setBookmarked(false); toast.success("Removed");
    } else {
      await api.post(`/bookmarks`, { article_id }); setBookmarked(true); toast.success("Bookmarked");
    }
  };

  const askInChat = async () => {
    if (!user) { navigate("/login"); return; }
    const q = article?.question?.[lang] || article?.question?.en;
    const { data } = await api.post("/conversations", { title: q?.slice(0, 60), language: lang });
    navigate(`/chat/${data.conversation_id}`, { state: { initialQuestion: q } });
  };

  if (!article) {
    return <AppShell><div className="p-8 text-[#868E96]">{t(lang,"common.loading")}</div></AppShell>;
  }

  return (
    <AppShell>
      <Link to="/knowledge" className="inline-flex items-center gap-2 text-sm text-[#495057] hover:text-[#0A1128]" data-testid="article-back">
        <ArrowLeft className="w-4 h-4" /> {t(lang,"common.back")}
      </Link>

      <div className="mt-6 max-w-3xl">
        <p className="text-xs tracking-[0.24em] uppercase text-[#C65D47]">{article.law_reference}</p>
        <h1 className="mt-3 font-display text-3xl md:text-4xl leading-tight" data-testid="article-title">
          {article.question?.[lang] || article.question?.en}
        </h1>
        <div className="mt-8 prose-base text-[#0A1128] leading-relaxed whitespace-pre-wrap" data-testid="article-body">
          {article.answer?.[lang] || article.answer?.en}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <button onClick={toggle} className="inline-flex items-center gap-2 border border-[#0A1128] px-4 py-2 hover:bg-[#F8F9FA]" data-testid="article-bookmark">
            {bookmarked ? <><BookmarkCheck className="w-4 h-4 text-[#C65D47]" /> {t(lang,"kb.bookmarked")}</> : <><BookmarkPlus className="w-4 h-4" /> {t(lang,"kb.bookmark")}</>}
          </button>
          <button onClick={askInChat} className="inline-flex items-center gap-2 bg-[#C65D47] text-white px-4 py-2 hover:bg-[#A94834]" data-testid="article-ask-lawpilot">
            <MessageSquare className="w-4 h-4" /> Ask Law Pilot about this
          </button>
        </div>
        <p className="mt-10 text-xs text-[#868E96] border-t border-[#E9ECEF] pt-4">{t(lang,"common.disclaimer")}</p>
      </div>
    </AppShell>
  );
}
